/**
 * Chart data transformations — maps backend chart_config + flat data rows
 * into the series shape that UniversalChart expects.
 *
 * Backend contract:
 *   {
 *     chart_config: {
 *       default_chart_style: ChartStyle,
 *       x_axis: { key, label?, type? },
 *       y_axis: [{ key, label }, ...],
 *     },
 *     data: [{ <x_axis.key>, <y_axis[0].key>, <y_axis[1].key>, ... }, ...]
 *   }
 *
 * Each y_axis entry becomes a separate series. For each series, data is a
 * list of { name, y } points where `name` is the x-axis value and `y` is the
 * numeric value of that series' column for that row.
 */

import {CHART_KIND_GROUPS} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';
import type {ChartKind} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';

import {formatToCustomDateString} from 'src/utils/analytics-genesis';
import type {ChartAxis, ChartConfig, ChartStyle} from 'src/types/byod';

// ---------------------------------------------------------------------------
// Kind groups — sourced from the design system so BYOD stays in sync with
// UniversalChart's compatibility matrix.
// ---------------------------------------------------------------------------

const CATEGORICAL_KIND_KEYS: ReadonlyArray<ChartKind> =
  CHART_KIND_GROUPS.CATEGORICAL.map((opt) => opt.key);
const PROPORTION_KIND_KEYS: ReadonlyArray<ChartKind> =
  CHART_KIND_GROUPS.PROPORTION.map((opt) => opt.key);
const FLOW_KIND_KEYS: ReadonlyArray<ChartKind> = CHART_KIND_GROUPS.FLOW.map(
  (opt) => opt.key,
);

export function isCategoricalKind(style: ChartStyle): boolean {
  return CATEGORICAL_KIND_KEYS.includes(style);
}

export function isProportionKind(style: ChartStyle): boolean {
  return PROPORTION_KIND_KEYS.includes(style);
}

export function isFlowKind(style: ChartStyle): boolean {
  return FLOW_KIND_KEYS.includes(style);
}

export function getKindsInGroup(style: ChartStyle): ReadonlyArray<ChartStyle> {
  if (isProportionKind(style)) {
    return PROPORTION_KIND_KEYS as ReadonlyArray<ChartStyle>;
  }
  if (isFlowKind(style)) {
    return FLOW_KIND_KEYS as ReadonlyArray<ChartStyle>;
  }
  return CATEGORICAL_KIND_KEYS as ReadonlyArray<ChartStyle>;
}

// ---------------------------------------------------------------------------
// Row coercion helpers
// ---------------------------------------------------------------------------

/** Coerces a row value to a number, defaulting to 0 for null/undefined/NaN. */
function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Coerces a row value to a string, using empty string for null/undefined. */
function toLabel(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

/**
 * Formats a value for the x-axis.
 * When the axis is typed as `date`, formats with agency timezone using the
 * same `'MMM D, YYYY'` style as the rest of the analytics module
 * (see metrics-and-chart.tsx, job-status-age-chart.tsx, etc.) so BYOD
 * charts stay visually consistent with built-in dashboards.
 */
function formatAxisValue(
  value: unknown,
  axis: ChartAxis | undefined,
  options: {agencyTimezone?: string; language?: string},
): string {
  if (value == null) {
    return '';
  }
  if (axis?.type === 'date') {
    return formatToCustomDateString(
      String(value),
      options.agencyTimezone,
      'MMM D, YYYY',
      options.language,
    );
  }
  return toLabel(value);
}

// ---------------------------------------------------------------------------
// Series types
// ---------------------------------------------------------------------------

export type CategoricalSeriesPoint = {name: string; y: number};
export type CategoricalSeries = {
  name: string;
  data: CategoricalSeriesPoint[];
};

export type ProportionSeries = {
  name?: string;
  data: CategoricalSeriesPoint[];
};

export type FlowLink = {from: string; to: string; weight: number};
export type FlowNode = {id: string; name: string};
export type FlowSeries = {data: FlowLink[]; nodes: FlowNode[]};

export type BuildSeriesOptions = {
  agencyTimezone?: string;
  language?: string;
};

// ---------------------------------------------------------------------------
// Transformations
// ---------------------------------------------------------------------------

/**
 * Categorical charts (column, line, stackedBar, spider).
 * One series per y_axis entry. Rows with null x-axis value are filtered out.
 */
export function buildCategoricalSeries(
  data: ReadonlyArray<Record<string, unknown>>,
  config: ChartConfig,
  options: BuildSeriesOptions = {},
): CategoricalSeries[] {
  const xKey = config.x_axis?.key;
  const yAxes = config.y_axis ?? [];

  if (!xKey || yAxes.length === 0) {
    return [];
  }

  const validRows = data.filter((row) => row[xKey] != null);

  return yAxes.map((series) => ({
    name: series.label,
    data: validRows.map((row) => ({
      name: formatAxisValue(row[xKey], config.x_axis, options),
      y: toNumber(row[series.key]),
    })),
  }));
}

/**
 * Proportion charts (donut, funnel).
 * Single series — each row becomes one slice. x_axis.key = slice name,
 * y_axis[0].key = slice value.
 */
export function buildProportionSeries(
  data: ReadonlyArray<Record<string, unknown>>,
  config: ChartConfig,
  options: BuildSeriesOptions = {},
): ProportionSeries[] {
  const xKey = config.x_axis?.key;
  const yAxes = config.y_axis ?? [];

  if (!xKey || yAxes.length === 0) {
    return [];
  }

  const valueKey = yAxes[0].key;
  const validRows = data.filter((row) => row[xKey] != null);

  return [
    {
      name: yAxes[0].label,
      data: validRows.map((row) => ({
        name: formatAxisValue(row[xKey], config.x_axis, options),
        y: toNumber(row[valueKey]),
      })),
    },
  ];
}

/**
 * Flow charts (sankey). Rows must have `from`, `to`, and optional `weight`.
 * Nodes are auto-derived from unique `from`/`to` values.
 */
export function buildFlowSeries(
  data: ReadonlyArray<Record<string, unknown>>,
): FlowSeries[] {
  const links: FlowLink[] = data
    .filter((row) => row.from != null && row.to != null)
    .map((row) => ({
      from: toLabel(row.from),
      to: toLabel(row.to),
      weight: toNumber(row.weight),
    }));

  const nodeIds = new Set<string>();
  for (const link of links) {
    nodeIds.add(link.from);
    nodeIds.add(link.to);
  }
  const nodes: FlowNode[] = Array.from(nodeIds).map((id) => ({id, name: id}));

  return [{data: links, nodes}];
}
